import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, X, Calendar, ShieldCheck, Repeat, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function fmtINR(n) {
  if (!n && n !== 0) return "—";
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function SubscriptionRenewalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const justRenewed = searchParams.get("renewed") === "1";

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [renewedSuccess, setRenewedSuccess] = useState(false);

  async function loadSub() {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/subscriptions/${token}`);
      setSub(r.data.subscription);
    } catch (e) {
      setError(e?.response?.data?.detail || "This link is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSub(); }, [token]);  // eslint-disable-line react-hooks/exhaustive-deps

  // If returning from Cashfree (?renewed=1), poll the verify endpoint
  useEffect(() => {
    if (!justRenewed || !token) return;
    setVerifying(true);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const r = await axios.get(`${API}/subscriptions/${token}/verify`);
        if (r.data?.paid) {
          setRenewedSuccess(true);
          setVerifying(false);
          clearInterval(interval);
          loadSub();
        }
      } catch (_e) { /* ignore */ }
      if (attempts >= 15) { setVerifying(false); clearInterval(interval); }
    }, 2000);
    return () => clearInterval(interval);
  }, [justRenewed, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function payNow() {
    setPaying(true);
    setError("");
    try {
      const r = await axios.post(`${API}/subscriptions/${token}/renew`);
      if (r.data?.already_paid) {
        setRenewedSuccess(true);
        await loadSub();
        return;
      }
      const session = r.data?.payment_session_id;
      if (!session) throw new Error("No payment session");
      const { load } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await load({ mode: "production" });
      cashfree.checkout({ paymentSessionId: session, redirectTarget: "_self" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start payment. Please retry or email support@shansoai.com.");
      setPaying(false);
    }
  }

  async function cancelNow() {
    setCancelling(true);
    setError("");
    try {
      await axios.post(`${API}/subscriptions/${token}/cancel`, { reason: cancelReason || null });
      setConfirmCancel(false);
      await loadSub();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not cancel. Please email support@shansoai.com.");
    } finally {
      setCancelling(false);
    }
  }

  // ─────────── render ───────────
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <Loader2 size={32} className="animate-spin text-cyan-400" />
      </div>
    );
  }
  if (error && !sub) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center" data-testid="sub-error-screen">
          <AlertTriangle size={36} className="text-red-400 mx-auto mb-3"/>
          <p className="text-white font-bold mb-2">Link not found</p>
          <p className="text-zinc-400 text-sm">{error}</p>
          <a href="mailto:support@shansoai.com" className="inline-block mt-4 text-cyan-300 text-sm hover:underline">Email support@shansoai.com</a>
        </div>
      </div>
    );
  }

  const plan = sub?.plan || {};
  const customer = sub?.customer || {};
  const isCancelled = sub?.status === "cancelled";
  const alreadyPaid = !!sub?.renewal_paid_at || renewedSuccess;
  const daysLeft = sub?.renews_at ? Math.ceil((new Date(sub.renews_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="min-h-screen bg-zinc-950 text-white px-4 py-10 sm:py-16" data-testid="renewal-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-emerald-400 flex items-center justify-center font-bold text-black">S</div>
          <div>
            <p className="text-white font-bold">ShansoAI</p>
            <p className="text-zinc-500 text-xs">Annual plan management</p>
          </div>
        </div>

        {/* Success banner */}
        {alreadyPaid && (
          <div className="bg-emerald-500/10 border border-emerald-400/40 rounded-2xl p-6 mb-6" data-testid="renewal-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 size={28} className="text-emerald-300 shrink-0 mt-0.5"/>
              <div>
                <p className="text-emerald-200 font-bold text-lg">Renewal complete ✨</p>
              <p className="text-zinc-300 text-sm mt-1">Your annual plan is active until <b className="text-emerald-300">{fmtDate(sub?.renews_at)}</b>. A GST invoice will follow within 24 hours.</p>
              </div>
            </div>
          </div>
        )}

        {verifying && !alreadyPaid && (
          <div className="bg-zinc-900 border border-cyan-500/40 rounded-2xl p-5 mb-6 flex items-center gap-3" data-testid="verifying-payment">
            <Loader2 size={20} className="animate-spin text-cyan-400"/>
            <p className="text-zinc-300 text-sm">Confirming your payment with Cashfree…</p>
          </div>
        )}

        {/* Plan card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="flex items-center gap-2 text-cyan-300 text-xs uppercase tracking-wider font-bold mb-2">
            <Repeat size={14}/> Annual subscription
          </div>
          <p className="text-2xl font-bold text-white mb-1" data-testid="sub-plan-name">{plan.name}</p>
          <p className="text-zinc-400 text-sm mb-5">Hi <b className="text-white" data-testid="sub-customer-name">{customer.full_name || "there"}</b>, here&apos;s your subscription at a glance.</p>

          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Renewal price</p>
              <p className="text-2xl font-bold text-emerald-300 mt-1" data-testid="renewal-price">{fmtINR(plan.renewal_price)}/yr</p>
              <p className="text-[11px] text-zinc-500 mt-1">~ {fmtINR(plan.monthly_equiv)}/mo equivalent</p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Renews on</p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="renews-at">{fmtDate(sub?.renews_at)}</p>
              {daysLeft != null && !isCancelled && !alreadyPaid && (
                <p className={`text-[11px] mt-1 ${daysLeft <= 7 ? "text-amber-300" : "text-zinc-500"}`}>
                  {daysLeft > 0 ? `In ${daysLeft} day${daysLeft===1?"":"s"}` : daysLeft === 0 ? "Today" : `${-daysLeft} day${-daysLeft===1?"":"s"} overdue`}
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Included every year</p>
            <ul className="space-y-1.5">
              {(plan.includes || []).map((inc, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                  <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5"/>
                  <span>{inc}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Action buttons */}
        {isCancelled ? (
          <div className="bg-zinc-900 border border-red-500/30 rounded-2xl p-6 text-center" data-testid="cancelled-banner">
            <X size={32} className="text-red-400 mx-auto mb-2"/>
            <p className="text-red-200 font-bold">Renewal cancelled</p>
            <p className="text-zinc-400 text-sm mt-1">Your plan will not auto-renew. Want it back? Email <a href="mailto:support@shansoai.com" className="text-cyan-300">support@shansoai.com</a>.</p>
          </div>
        ) : alreadyPaid ? (
          <div className="text-center">
            <a href="/" className="text-cyan-300 text-sm hover:underline">← Back to shansoai.com</a>
          </div>
        ) : (
          <>
            <button
              onClick={payNow}
              disabled={paying}
              data-testid="renew-now-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-400 text-black font-bold text-base hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed mb-3"
            >
              {paying ? <><Loader2 size={18} className="animate-spin"/> Starting payment…</> : <>Renew now — {fmtINR(plan.renewal_price)}/yr →</>}
            </button>
            <p className="text-center text-[11px] text-zinc-500 mb-6 flex items-center justify-center gap-1">
              <ShieldCheck size={11}/> Cashfree-secured · 7-day refund window · GST invoice on request
            </p>

            {!confirmCancel ? (
              <div className="text-center">
                <button
                  onClick={() => setConfirmCancel(true)}
                  data-testid="cancel-link"
                  className="text-zinc-500 text-xs hover:text-red-300 underline"
                >
                  Don&apos;t want to renew? Cancel auto-renewal
                </button>
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5" data-testid="cancel-form">
                <p className="text-white font-bold text-sm mb-2">Cancel your auto-renewal?</p>
                <p className="text-zinc-400 text-xs mb-3">Your services stay active until <b className="text-zinc-200">{fmtDate(sub?.renews_at)}</b>. After that, the plan stops. You can always come back and start a fresh plan later.</p>
                <textarea
                  rows={2}
                  value={cancelReason}
                  onChange={(e)=>setCancelReason(e.target.value)}
                  placeholder="Reason (optional — helps us improve)"
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:border-red-400 focus:outline-none resize-none mb-3"
                  data-testid="cancel-reason-input"
                />
                <div className="flex gap-2">
                  <button
                    onClick={cancelNow}
                    disabled={cancelling}
                    data-testid="confirm-cancel-btn"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-red-500/15 border border-red-400/40 text-red-200 text-xs font-bold hover:bg-red-500/25 disabled:opacity-40"
                  >
                    {cancelling ? <><Loader2 size={12} className="animate-spin"/> Cancelling…</> : <>Yes, cancel renewal</>}
                  </button>
                  <button
                    onClick={()=>setConfirmCancel(false)}
                    data-testid="back-from-cancel"
                    className="px-4 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-bold hover:border-zinc-500"
                  >
                    Never mind
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && <p className="text-red-300 text-sm mt-4 text-center" data-testid="action-error">⚠ {error}</p>}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-zinc-800 text-center">
          <p className="text-zinc-500 text-xs flex items-center justify-center gap-2">
            <Calendar size={12}/>
            Subscription <code className="text-zinc-400 font-mono" data-testid="sub-id">{sub?.subscription_id}</code> · <a href="mailto:support@shansoai.com" className="text-zinc-400 hover:text-cyan-300">support@shansoai.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
